import React, { useEffect, useState } from "react";
import {
  Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay,
  Box, Text, FormControl, FormLabel, Input, Textarea, Select, Button, HStack, VStack,
  Tag, Badge, Avatar, AvatarGroup, useToast, Spinner, IconButton, Tooltip, Flex,
  Editable, EditableInput, EditablePreview, Wrap, WrapItem, Heading, Divider,
  Tabs, TabList, TabPanels, Tab, TabPanel, Checkbox,
} from "@chakra-ui/react";
import { DeleteIcon, AddIcon } from "@chakra-ui/icons";
import { api } from "../../lib/api";
import { FUNCTIONAL_ROLE_LABELS, FUNCTIONAL_ROLE_COLORS } from "./df-constants";
import RichTextEditor from "../../Components/RichTextEditor";
import FilePicker from "../../Components/FilePicker";
import TaskChatPanel from "../../Components/TaskChatPanel";
import { API_BASE } from "../../lib/api";

const STATUSES = ["todo", "in_progress", "review", "done"];
const PRIORITIES = ["low", "normal", "high", "urgent"];

export default function TaskDetailDrawer({ isOpen, onClose, taskId, onChanged, projectId, allUsers = [] }) {
  const toast = useToast();
  const [task, setTask] = useState(null);
  const [subtasks, setSubtasks] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newCheckItem, setNewCheckItem] = useState("");
  const me = localStorage.getItem("userID");

  useEffect(() => {
    if (!isOpen || !taskId) { setTask(null); setSubtasks([]); setFiles([]); return; }
    setLoading(true);
    Promise.all([
      api(`/tasks/${taskId}`),
      api(`/tasks/${taskId}/subtasks`).catch(() => ({ subtasks: [] })),
      api(`/files/by-task/${taskId}`).catch(() => ({ files: [] })),
    ])
      .then(([d, s, fl]) => { setTask(d.task); setSubtasks(s.subtasks || []); setFiles(fl.files || []); })
      .catch((e) => toast({ title: e.message, status: "error" }))
      .finally(() => setLoading(false));
  }, [isOpen, taskId, toast]);

  async function patch(body) {
    try {
      const d = await api(`/tasks/${taskId}`, { method: "PATCH", body: JSON.stringify(body) });
      setTask(d.task);
      if (onChanged) onChanged(d.task);
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function addComment() {
    if (!newComment.trim()) return;
    try {
      const d = await api(`/tasks/${taskId}/comments`, { method: "POST", body: JSON.stringify({ text: newComment }) });
      setTask((t) => ({ ...t, comments: d.comments }));
      setNewComment("");
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function removeComment(commentId) {
    try {
      await api(`/tasks/${taskId}/comments/${commentId}`, { method: "DELETE" });
      setTask((t) => ({ ...t, comments: t.comments.filter((c) => c._id !== commentId) }));
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function deleteTask() {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api(`/tasks/${taskId}`, { method: "DELETE" });
      toast({ title: "Task deleted", status: "success" });
      if (onChanged) onChanged(null);
      onClose();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function toggleAssignee(uid) {
    const has = (task.assignees || []).some((a) => String(a._id || a) === String(uid));
    const newList = has
      ? task.assignees.filter((a) => String(a._id || a) !== String(uid)).map((a) => a._id || a)
      : [...task.assignees.map((a) => a._id || a), uid];
    patch({ assignees: newList });
  }

  function addLabel() {
    const v = newLabel.trim();
    if (!v) return;
    if ((task.labels || []).includes(v)) { setNewLabel(""); return; }
    patch({ labels: [...(task.labels || []), v] });
    setNewLabel("");
  }

  function removeLabel(l) {
    patch({ labels: (task.labels || []).filter((x) => x !== l) });
  }

  async function toggleWatch() {
    const watching = (task.watchers || []).some((w) => String(w._id || w) === String(me));
    try {
      await api(`/tasks/${taskId}/watchers/me`, { method: watching ? "DELETE" : "POST" });
      const d = await api(`/tasks/${taskId}`);
      setTask(d.task);
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  const watching = task && (task.watchers || []).some((w) => String(w._id || w) === String(me));

  return (
    <Drawer isOpen={isOpen} placement="right" size="lg" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" pb={3}>
          {loading || !task ? "Loading..." : (
            <Editable defaultValue={task.title} onSubmit={(v) => v && v !== task.title && patch({ title: v })}>
              <EditablePreview fontSize="lg" />
              <EditableInput fontSize="lg" />
            </Editable>
          )}
        </DrawerHeader>
        <DrawerBody>
          {loading || !task ? <Spinner /> : (
            <VStack align="stretch" spacing={5}>
              <HStack>
                <Select value={task.status} onChange={(e) => patch({ status: e.target.value })} maxW="160px">
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </Select>
                <Select value={task.priority} onChange={(e) => patch({ priority: e.target.value })} maxW="140px">
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </Select>
                <Box flex="1" />
                <Tooltip label={watching ? "Stop watching" : "Watch this task"}>
                  <Button size="sm" variant="outline" onClick={toggleWatch}>{watching ? "Watching" : "Watch"}</Button>
                </Tooltip>
                <IconButton size="sm" colorScheme="red" variant="ghost" icon={<DeleteIcon />} aria-label="delete" onClick={deleteTask} />
              </HStack>

              <FormControl>
                <FormLabel fontSize="xs" textTransform="uppercase" color="gray.600">Description</FormLabel>
                <DescriptionEditor task={task} onSave={(html) => patch({ description: html })} />
              </FormControl>

              <Box>
                <Heading size="xs" textTransform="uppercase" color="gray.600" mb={2}>Files ({files.length})</Heading>
                <FilePicker taskId={taskId} files={files} onChange={setFiles} />
              </Box>

              {/* Phase 16: per-task chat removed; use task comments + project channel */}

              <HStack align="start" spacing={6}>
                <FormControl flex="1">
                  <FormLabel fontSize="xs" textTransform="uppercase" color="gray.600">Due date</FormLabel>
                  <Input
                    type="date"
                    value={task.dueDate ? task.dueDate.slice(0, 10) : ""}
                    onChange={(e) => patch({ dueDate: e.target.value || null })}
                  />
                </FormControl>
                <FormControl flex="1">
                  <FormLabel fontSize="xs" textTransform="uppercase" color="gray.600">Project</FormLabel>
                  <Box p={2} bg="gray.50" rounded="md" fontSize="sm" minH="40px">
                    {task.projectId ? (
                      <Tag bg={task.projectId.color || "gray.300"} color="white">{task.projectId.name}</Tag>
                    ) : <Text color="gray.400">No project</Text>}
                  </Box>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="xs" textTransform="uppercase" color="gray.600">Assignees</FormLabel>
                <Wrap spacing={2}>
                  {(task.assignees || []).map((a) => (
                    <WrapItem key={a._id || a}>
                      <HStack bg="gray.100" rounded="md" px={2} py={1}>
                        <Avatar size="2xs" name={a.username || ""} />
                        <Text fontSize="xs">{a.username}</Text>
                        <Box as="button" fontSize="xs" color="gray.500" onClick={() => toggleAssignee(a._id || a)}>×</Box>
                      </HStack>
                    </WrapItem>
                  ))}
                  <WrapItem>
                    <Select size="xs" placeholder="+ add" maxW="180px" onChange={(e) => { if (e.target.value) toggleAssignee(e.target.value); e.target.value = ""; }}>
                      {(allUsers || []).filter((u) => !(task.assignees || []).some((a) => String(a._id || a) === String(u._id))).map((u) => (
                        <option key={u._id} value={u._id}>{u.username} ({u.email})</option>
                      ))}
                    </Select>
                  </WrapItem>
                </Wrap>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs" textTransform="uppercase" color="gray.600">Labels</FormLabel>
                <Wrap spacing={2}>
                  {(task.labels || []).map((l) => (
                    <WrapItem key={l}>
                      <Tag>{l} <Box as="button" ml={1} onClick={() => removeLabel(l)}>×</Box></Tag>
                    </WrapItem>
                  ))}
                  <WrapItem>
                    <HStack>
                      <Input size="sm" placeholder="add label" value={newLabel} maxW="140px" onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addLabel()} />
                    </HStack>
                  </WrapItem>
                </Wrap>
              </FormControl>

              <Divider />

              {/* Checklist */}
              <Box>
                <Flex justify="space-between" align="center" mb={2}>
                  <Heading size="xs" textTransform="uppercase" color="gray.600">
                    Checklist ({(task.checklist || []).filter((c) => c.done).length}/{(task.checklist || []).length})
                  </Heading>
                </Flex>
                {(task.checklist || []).length > 0 && (
                  <Box mb={2}>
                    {(() => {
                      const total = (task.checklist || []).length;
                      const done = (task.checklist || []).filter((c) => c.done).length;
                      const pct = total ? Math.round((done / total) * 100) : 0;
                      return (
                        <Box w="100%" h="3px" bg="gray.200" rounded="full" overflow="hidden">
                          <Box h="100%" bg="green.500" w={`${pct}%`} transition="width 0.2s" />
                        </Box>
                      );
                    })()}
                  </Box>
                )}
                <VStack align="stretch" spacing={1}>
                  {(task.checklist || []).map((it) => (
                    <HStack key={it._id} bg="gray.50" rounded="md" px={2} py={1.5}>
                      <Checkbox
                        isChecked={it.done}
                        onChange={async () => {
                          try {
                            const r = await api(`/tasks/${taskId}/checklist/${it._id}`, {
                              method: "PATCH",
                              body: JSON.stringify({ done: !it.done }),
                            });
                            setTask((t) => ({ ...t, checklist: r.checklist }));
                          } catch (e) { toast({ title: e.message, status: "error" }); }
                        }}
                      />
                      <Text fontSize="sm" flex="1" textDecoration={it.done ? "line-through" : "none"} color={it.done ? "gray.400" : "gray.700"}>
                        {it.label}
                      </Text>
                      <IconButton size="2xs" variant="ghost" icon={<DeleteIcon />} aria-label="delete"
                        onClick={async () => {
                          try {
                            const r = await api(`/tasks/${taskId}/checklist/${it._id}`, { method: "DELETE" });
                            setTask((t) => ({ ...t, checklist: r.checklist }));
                          } catch (e) { toast({ title: e.message, status: "error" }); }
                        }} />
                    </HStack>
                  ))}
                </VStack>
                <HStack mt={2}>
                  <Input
                    size="sm" placeholder="Add a checklist item..."
                    value={newCheckItem}
                    onChange={(e) => setNewCheckItem(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key !== "Enter" || !newCheckItem.trim()) return;
                      try {
                        const r = await api(`/tasks/${taskId}/checklist`, {
                          method: "POST",
                          body: JSON.stringify({ label: newCheckItem.trim() }),
                        });
                        setTask((t) => ({ ...t, checklist: r.checklist }));
                        setNewCheckItem("");
                      } catch (err) { toast({ title: err.message, status: "error" }); }
                    }}
                  />
                </HStack>
              </Box>

              {/* Subtasks */}
              {subtasks.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Heading size="xs" textTransform="uppercase" color="gray.600" mb={3}>
                      Subtasks ({subtasks.length})
                    </Heading>
                    <VStack align="stretch" spacing={1}>
                      {subtasks.map((s) => (
                        <HStack key={s._id} bg="gray.50" rounded="md" px={2} py={1.5}>
                          {s.department && (
                            <>
                              <Box w="6px" h="6px" rounded="full" bg={FUNCTIONAL_ROLE_COLORS[s.department] || "gray.400"} flexShrink={0} />
                              <Text fontSize="11px" fontWeight={600} color="gray.700" w="120px" flexShrink={0}>
                                {FUNCTIONAL_ROLE_LABELS[s.department]}
                              </Text>
                            </>
                          )}
                          <Text fontSize="sm" flex="1" noOfLines={1} textDecoration={s.status === "done" ? "line-through" : "none"}>
                            {s.title}
                          </Text>
                          <Text fontSize="11px" color="gray.500">{(s.assignees && s.assignees[0]?.username) || "Unassigned"}</Text>
                          <Badge fontSize="10px" colorScheme={s.status === "done" ? "green" : s.status === "in_progress" ? "blue" : "gray"}>
                            {s.status.replace("_", " ")}
                          </Badge>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                </>
              )}

              <Divider />

              {/* Status summaries (rich) */}
              {(task.statusSummaries || []).length > 0 && (
                <Box>
                  <Heading size="xs" textTransform="uppercase" color="gray.600" mb={3}>
                    Status updates ({task.statusSummaries.length})
                  </Heading>
                  <VStack align="stretch" spacing={3}>
                    {(task.statusSummaries || []).map((s) => (
                      <HStack key={s._id} align="start" spacing={3}>
                        <Avatar size="sm" name={s.author?.username || ""} />
                        <Box flex="1" bg="blue.50" rounded="md" p={3}>
                          <HStack justify="space-between" mb={1}>
                            <Text fontSize="xs" fontWeight={600}>{s.author?.username || "—"}</Text>
                            <Text fontSize="2xs" color="gray.500">{new Date(s.createdAt).toLocaleString()}</Text>
                          </HStack>
                          <div dangerouslySetInnerHTML={{ __html: s.text }} className="tiptap-content prose prose-sm" />
                        </Box>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              )}

              <Divider />

              <Box>
                <Heading size="xs" textTransform="uppercase" color="gray.600" mb={3}>Comments ({(task.comments || []).length})</Heading>
                <VStack align="stretch" spacing={3}>
                  {(task.comments || []).map((c) => {
                    const mine = String(c.author?._id || c.author) === String(me);
                    return (
                      <HStack key={c._id} align="start" spacing={3}>
                        <Avatar size="sm" name={c.author?.username || ""} />
                        <Box flex="1" bg="gray.50" rounded="md" p={3}>
                          <HStack justify="space-between" mb={1}>
                            <Text fontSize="xs" fontWeight={600}>{c.author?.username || "—"}</Text>
                            <HStack spacing={2}>
                              <Text fontSize="2xs" color="gray.500">{new Date(c.createdAt).toLocaleString()}</Text>
                              {mine && <IconButton size="2xs" variant="ghost" icon={<DeleteIcon />} aria-label="delete" onClick={() => removeComment(c._id)} />}
                            </HStack>
                          </HStack>
                          {/^\s*</.test(c.text) ? (
                            <div dangerouslySetInnerHTML={{ __html: c.text }} className="tiptap-content prose prose-sm" />
                          ) : (
                            <Text fontSize="sm" whiteSpace="pre-wrap">{c.text}</Text>
                          )}
                        </Box>
                      </HStack>
                    );
                  })}
                  {(task.comments || []).length === 0 && <Text fontSize="sm" color="gray.500">No comments yet.</Text>}
                </VStack>
                <Box mt={3}>
                  <CommentComposer onPost={async (html) => {
                    if (!html || html === "<p></p>") return;
                    try {
                      const r = await api(`/tasks/${taskId}/comments`, { method: "POST", body: JSON.stringify({ text: html }) });
                      setTask((t) => ({ ...t, comments: r.comments }));
                    } catch (e) { toast({ title: e.message, status: "error" }); }
                  }} />
                </Box>
              </Box>

              <Box pt={3} borderTop="1px solid" borderColor="gray.200" fontSize="xs" color="gray.500">
                <Flex justify="space-between">
                  <Text>Created by {task.creatorUser?.username || "—"}</Text>
                  <Text>{new Date(task.createdAt).toLocaleString()}</Text>
                </Flex>
              </Box>
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

function DescriptionEditor({ task, onSave }) {
  const [html, setHtml] = React.useState(task.description || "");
  const [dirty, setDirty] = React.useState(false);
  React.useEffect(() => { setHtml(task.description || ""); setDirty(false); }, [task._id]); // eslint-disable-line
  return (
    <Box>
      <RichTextEditor
        value={html}
        onChange={(v) => { setHtml(v); setDirty(v !== (task.description || "")); }}
        placeholder="Add a description... use @ to mention"
        minHeight={100}
      />
      {dirty && (
        <HStack mt={2}>
          <Button size="xs" colorScheme="blue" onClick={async () => { await onSave(html); setDirty(false); }}>Save</Button>
          <Button size="xs" variant="ghost" onClick={() => { setHtml(task.description || ""); setDirty(false); }}>Cancel</Button>
        </HStack>
      )}
    </Box>
  );
}

function CommentComposer({ onPost }) {
  const [html, setHtml] = React.useState("");
  const editorRef = React.useRef(null);
  const empty = !html || html === "<p></p>";
  return (
    <Box>
      <RichTextEditor
        ref={editorRef}
        value={html}
        onChange={setHtml}
        placeholder="Write a comment... type @ to mention someone"
        minHeight={70}
        compact
      />
      <HStack mt={2} justify="flex-end">
        <Button
          size="sm"
          colorScheme="blue"
          isDisabled={empty}
          onClick={async () => {
            await onPost(html);
            setHtml("");
            editorRef.current?.clear();
          }}
        >
          Post
        </Button>
      </HStack>
    </Box>
  );
}
